import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Wine, Loader2, Sparkles, Leaf } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { designCocktails } from "@/lib/api";

const eventTypes = ["Beachfront Wedding", "Backyard Party", "Boat Party", "Corporate Event", "Birthday"];
const packages = [{ id: "essential", label: "Essential · 4" }, { id: "premium", label: "Premium · 6" }, { id: "ultimate", label: "Ultimate · 8" }];

export default function Cocktails() {
  const [form, setForm] = useState({ event_type: "Beachfront Wedding", guest_count: 50, theme: "", flavours: "", include_mocktails: true, package_id: "premium" });
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, follow";
    meta.setAttribute("data-page-noindex", "cocktails");
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    setLoading(true); setMenu(null);
    try {
      const data = await designCocktails({ ...form, guest_count: parseInt(form.guest_count) || 40 });
      setMenu(data);
    } catch {
      setMenu({ error: true });
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-cream min-h-screen">
      <Nav solid />
      <section className="max-w-6xl mx-auto px-5 md:px-8 pt-32 md:pt-40 pb-24">
        <p className="overline text-xs text-coral mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4" /> Cocktail Menu Advisor</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ocean-deep leading-[1.02] tracking-tight max-w-3xl">Design your signature menu.</h1>
        <p className="text-ocean/70 mt-4 max-w-xl">Tell us about your event and we'll craft a bespoke cocktail menu with native Australian botanicals — recipes, garnishes and all.</p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-14">
          <div className="lg:col-span-2 space-y-5 rounded-3xl bg-white border border-sand p-6 h-fit lg:sticky lg:top-28">
            <div>
              <label className="overline text-xs text-ocean block mb-2">Event type</label>
              <Select value={form.event_type} onValueChange={(v) => upd("event_type", v)}>
                <SelectTrigger data-testid="cocktail-event-type" className="h-11 bg-cream border-sand rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{eventTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline text-xs text-ocean block mb-2">Guests</label>
                <Input data-testid="cocktail-guests" type="number" min="1" value={form.guest_count} onChange={(e) => upd("guest_count", e.target.value)} className="h-11 bg-cream border-sand rounded-xl" />
              </div>
              <div>
                <label className="overline text-xs text-ocean block mb-2">Package</label>
                <Select value={form.package_id} onValueChange={(v) => upd("package_id", v)}>
                  <SelectTrigger data-testid="cocktail-package" className="h-11 bg-cream border-sand rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="overline text-xs text-ocean block mb-2">Theme / vibe</label>
              <Input data-testid="cocktail-theme" placeholder="e.g. sunset boho, tropical glam" value={form.theme} onChange={(e) => upd("theme", e.target.value)} className="h-11 bg-cream border-sand rounded-xl" />
            </div>
            <div>
              <label className="overline text-xs text-ocean block mb-2">Flavour preferences</label>
              <Input data-testid="cocktail-flavours" placeholder="e.g. citrus, spicy, not too sweet" value={form.flavours} onChange={(e) => upd("flavours", e.target.value)} className="h-11 bg-cream border-sand rounded-xl" />
            </div>
            <div className="flex items-center justify-between py-1">
              <span className="text-sm text-ocean-deep">Include mocktails</span>
              <Switch data-testid="cocktail-mocktails" checked={form.include_mocktails} onCheckedChange={(v) => upd("include_mocktails", v)} />
            </div>
            <button data-testid="cocktail-generate" onClick={generate} disabled={loading} className="w-full rounded-full bg-coral text-white py-4 overline text-xs hover:bg-[#c9633f] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wine className="w-4 h-4" />}
              {loading ? "Designing menu..." : "Design My Menu"}
            </button>
          </div>

          <div className="lg:col-span-3">
            {!menu && !loading && (
              <div className="h-full min-h-[400px] rounded-3xl border border-dashed border-sand grid place-items-center text-center p-10">
                <div><Wine className="w-10 h-10 text-coral/40 mx-auto mb-4" /><p className="text-ocean/50 max-w-xs">Your bespoke cocktail menu will appear here.</p></div>
              </div>
            )}
            {loading && (
              <div className="h-full min-h-[400px] rounded-3xl bg-white border border-sand grid place-items-center">
                <div className="text-center"><Loader2 className="w-8 h-8 text-coral animate-spin mx-auto mb-4" /><p className="text-ocean/60">Shaking up something special...</p></div>
              </div>
            )}
            {menu?.error && <div className="rounded-3xl bg-white border border-sand p-8 text-ocean/70">Couldn&apos;t design a menu right now — please try again.</div>}
            {menu && !menu.error && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="cocktail-menu" className="rounded-3xl bg-white border border-sand overflow-hidden">
                <div className="bg-ocean-deep text-white p-8">
                  <h2 className="font-serif text-3xl md:text-4xl">{menu.menu_title}</h2>
                  <p className="text-white/70 mt-2 text-sm">{menu.intro}</p>
                </div>
                <div className="p-6 md:p-8 space-y-6">
                  {menu.cocktails?.map((c, i) => (
                    <div key={i} className="border-b border-sand pb-6 last:border-0">
                      <div className="flex items-baseline gap-3">
                        <span className="font-serif text-coral text-lg">{String(i + 1).padStart(2, "0")}</span>
                        <h3 className="font-serif text-2xl text-ocean-deep">{c.name}</h3>
                      </div>
                      <p className="text-ocean/75 text-sm mt-1 ml-8">{c.description}</p>
                      <div className="ml-8 mt-3 flex flex-wrap gap-2">
                        {c.ingredients?.map((ing, j) => <span key={j} className="text-xs bg-seafoam text-ocean px-3 py-1 rounded-full">{ing}</span>)}
                      </div>
                      <p className="ml-8 mt-3 text-xs text-ocean/60 flex items-center gap-2"><Leaf className="w-3.5 h-3.5 text-coral" /> {c.garnish} · {c.glass}</p>
                    </div>
                  ))}
                  {menu.mocktails?.length > 0 && (
                    <div className="pt-2">
                      <p className="overline text-xs text-coral mb-4">Zero-Proof</p>
                      {menu.mocktails.map((c, i) => (
                        <div key={i} className="mb-4">
                          <h3 className="font-serif text-xl text-ocean-deep">{c.name}</h3>
                          <p className="text-ocean/70 text-sm">{c.description}</p>
                          <p className="text-xs text-ocean/60 mt-1 flex items-center gap-2"><Leaf className="w-3.5 h-3.5 text-coral" /> {c.garnish}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {menu.local_note && <div className="rounded-2xl bg-seafoam p-5 text-sm text-ocean flex gap-3"><Leaf className="w-5 h-5 text-coral shrink-0" /> {menu.local_note}</div>}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
